"use client";

import { createContext, useCallback, useMemo, useState } from "react";
import Cookies from "js-cookie";
import dynamic from "next/dynamic";
import { InferAttributes } from "sequelize";
import { Users } from "@/database/models/users";
import { WalletBalances } from "@/database/models/wallet_balances";
import { authUser } from "@/actions/authUser";
import { fetchUser } from "@/fetch/fetch_user";

export const DashboardContext = createContext<DashboardContextProp>({
  unreadMsg: 0,
});

interface DashboardContextProp {
  unreadMsg: number,
  expandSidebar?: boolean;
  user?: InferAttributes<Users>,
  wallets?: InferAttributes<WalletBalances>[],
  setTheme?: (theme: "dark" | "light") => void;
  livechatOpen?: boolean,
  livechatTargetUserId?: number | null,
  toggleLivechat?: (expand: boolean) => void,
  openAdminLivechat?: (userId: number) => void,
  setExpand?: (expand: boolean) => void;
  updateUnreadMsg?: (value: number) => void;
  setLivechatTargetUserId?: (userId: number | null) => void;
  fetchUser?: () => Promise<InferAttributes<Users>>,
  fetchUserWallets?: () => Promise<any>,
}

export default function DashboardProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  var isDarkFromCookie = false;
  if (typeof window !== 'undefined') {
  } else {
    isDarkFromCookie = Cookies.get('theme') == 'dark';
  }

  const [state, setState] = useState<DashboardContextProp>({
    unreadMsg: 0,
    expandSidebar: false,
    livechatOpen: false,
    livechatTargetUserId: null,
  });


  const toggleLivechat = useCallback((value: boolean) => {
    setState((prev) =>
      prev.livechatOpen === value ? prev : { ...prev, livechatOpen: value }
    );
  }, []);

  const updateUnreadMsg = useCallback((value: number) => {
    setState((prev) =>
      prev.unreadMsg === value ? prev : { ...prev, unreadMsg: value }
    );
  }, []);

  const setLivechatTargetUserId = useCallback((userId: number | null) => {
    setState((prev) =>
      prev.livechatTargetUserId === userId
        ? prev
        : { ...prev, livechatTargetUserId: userId }
    );
  }, []);

  const openAdminLivechat = useCallback((userId: number) => {
    setState((prev) => ({
      ...prev,
      livechatTargetUserId: userId,
      livechatOpen: true,
    }));
  }, []);

  const value = useMemo(
    () => ({
      ...state,
      toggleLivechat,
      updateUnreadMsg,
      setLivechatTargetUserId,
      openAdminLivechat,
    }),
    [
      state,
      toggleLivechat,
      updateUnreadMsg,
      setLivechatTargetUserId,
      openAdminLivechat,
    ]
  );


  return (
    <DashboardContext.Provider
      value={value}
    >
      {children}
    </DashboardContext.Provider>
  );
}
